import { useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { SETUP_RETURN_EXCHANGE_CUSTOMIZATION_INSTRUCTIONS } from 'gql/mutations';

type SetupResult = {
  version: 1;
  storeId: string;
  status: 'READY' | 'REVIEW_REQUIRED' | 'UNAVAILABLE';
  definitionId: string | null;
  code:
    | 'READY'
    | 'INCOMPATIBLE_DEFINITION'
    | 'PIN_REQUIRED'
    | 'PROVIDER_UNAVAILABLE';
};
const UUID = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
const DEFINITION = /^gid:\/\/shopify\/MetafieldDefinition\/[1-9][0-9]*$/;
const KEYS = ['version', 'storeId', 'status', 'definitionId', 'code'];
function readSetup(value: unknown, storeId: string): SetupResult | null {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Object.prototype
  )
    return null;
  const fields = Object.getOwnPropertyDescriptors(value);
  if (
    Reflect.ownKeys(fields).length !== KEYS.length ||
    !KEYS.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(fields, key) &&
        'value' in fields[key] &&
        fields[key].enumerable,
    )
  )
    return null;
  const row = Object.fromEntries(KEYS.map((key) => [key, fields[key].value]));
  if (
    row.version !== 1 ||
    row.storeId !== storeId ||
    (row.definitionId !== null &&
      (typeof row.definitionId !== 'string' ||
        row.definitionId.length > 160 ||
        !DEFINITION.test(row.definitionId)))
  )
    return null;
  if (row.status === 'READY')
    return row.code === 'READY' && row.definitionId !== null
      ? (row as SetupResult)
      : null;
  if (row.status === 'REVIEW_REQUIRED')
    return ['INCOMPATIBLE_DEFINITION', 'PIN_REQUIRED'].includes(row.code)
      ? (row as SetupResult)
      : null;
  return row.status === 'UNAVAILABLE' && row.code === 'PROVIDER_UNAVAILABLE'
    ? (row as SetupResult)
    : null;
}
interface State {
  scope: object;
  pending: boolean;
  touched: boolean;
  result: SetupResult | null;
}
export default function CustomizationInstructionsSetup({
  storeId,
  disabled = false,
}: {
  storeId: string;
  disabled?: boolean;
}) {
  const owner = useRef({ storeId, pending: false }),
    mounted = useRef(true);
  if (owner.current.storeId !== storeId)
    owner.current = { storeId, pending: false };
  const scope = owner.current;
  const empty: State = { scope, pending: false, touched: false, result: null };
  const [state, setState] = useState<State>(empty);
  const current = state.scope === scope ? state : empty;
  const [write] = useMutation(SETUP_RETURN_EXCHANGE_CUSTOMIZATION_INSTRUCTIONS);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const live = () => mounted.current && owner.current === scope;
  async function prepare() {
    if (!live() || disabled || !UUID.test(storeId) || scope.pending) return;
    scope.pending = true;
    setState({ ...empty, touched: true, pending: true });
    try {
      const response = await write({ variables: { input: { storeId } } });
      if (!live()) return;
      const result = response.errors?.length
        ? null
        : readSetup(
            response.data?.setupReturnExchangeCustomizationInstructions,
            storeId,
          );
      setState({ scope, pending: false, touched: true, result });
    } catch {
      if (live()) setState({ ...empty, touched: true });
    } finally {
      if (live()) scope.pending = false;
    }
  }
  const ready = current.result?.status === 'READY';
  const message = current.pending
    ? 'Preparing Shopify order instructions…'
    : ready
    ? 'Shopify order instructions are ready.'
    : current.result?.code === 'PIN_REQUIRED'
    ? 'Make height instructions visible in Shopify order details, then check again.'
    : current.result?.code === 'INCOMPATIBLE_DEFINITION'
    ? 'An existing Shopify order field needs review. Contact ShipAid support before enabling customized replacements.'
    : current.touched
    ? 'Shopify instruction setup could not be verified. Check again before enabling customized replacements.'
    : 'Prepare the order field your team will read in Shopify before enabling customized replacements.';
  return (
    <section
      aria-label="Shopify height instructions setup"
      className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
    >
      <h3 className="text-sm font-semibold text-gray-900">
        Shopify order instructions
      </h3>
      <p className="text-xs leading-5 text-gray-600">
        This adds a readable height-instructions field to Shopify orders. Your
        team reviews it before releasing each customized replacement.
      </p>
      <p
        role={
          current.touched
            ? current.pending || ready
              ? 'status'
              : 'alert'
            : undefined
        }
        className="text-xs leading-5 text-gray-600"
      >
        {message}
      </p>
      <Button
        variant="outlined"
        disabled={disabled || current.pending || !UUID.test(storeId)}
        onClick={prepare}
      >
        {current.pending
          ? 'Preparing Shopify order instructions…'
          : current.touched
          ? 'Check Shopify order instructions'
          : 'Prepare Shopify order instructions'}
      </Button>
    </section>
  );
}
